import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, MapPin, Briefcase, Clock } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import CTASection from "@/components/CTASection";
import { CASE_STUDIES, INDUSTRIES } from "@/data/site";

const ALL = "All";

export default function CaseStudies() {
  const [filter, setFilter] = useState(ALL);
  const tags = [ALL, ...Array.from(new Set(CASE_STUDIES.map((c) => c.industry)))];
  const filtered = filter === ALL ? CASE_STUDIES : CASE_STUDIES.filter((c) => c.industry === filter);

  return (
    <div data-testid="page-case-studies">
      <section className="bg-white">
        <div className="sozos-container pt-12 md:pt-16 pb-10">
          <p className="eyebrow">Case Studies</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#0A1F44] mt-4 tracking-tight leading-[1.02] max-w-4xl">
            Outcomes our clients tell their boards about.
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mt-7 max-w-3xl leading-relaxed">
            Real engagements across Healthcare IT, AI, BFSI, SaaS, IT Services, Manufacturing and
            Logistics. Every program is designed to move the needle on pipeline, meetings and
            revenue — not vanity metrics.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="sozos-container pb-20 md:pb-24">
          <div className="flex flex-wrap gap-2 mb-12">
            {tags.map((t) => (
              <button
                key={t}
                data-testid={`case-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(t)}
                className={`px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold transition-all ${
                  filter === t
                    ? "bg-[#0A1F44] text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-[#1E40AF] hover:text-[#1E40AF]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="space-y-16 md:space-y-24">
            {filtered.map((c, i) => (
              <CaseStudyBlock key={c.slug} data={c} reverse={i % 2 === 1} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="sozos-container section-pad">
          <SectionHeader
            eyebrow="Verticals served"
            title="Programs delivered across twelve industries."
          />
          <div className="mt-10 flex flex-wrap gap-3">
            {INDUSTRIES.map((i) => (
              <span
                key={i.slug}
                className="px-4 py-2 bg-white border border-slate-200 rounded-sm text-sm text-slate-700"
              >
                {i.title}
              </span>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Want to be our next case study?" subtitle="Start your story" />
    </div>
  );
}

function CaseStudyBlock({ data, reverse, index }) {
  const d = data.detail;
  if (!d) return null;
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: Math.min(index * 0.04, 0.2) }}
      data-testid={`case-card-${data.slug}`}
      className="scroll-mt-24"
      id={data.slug}
    >
      <div className={`grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div className="lg:col-span-2">
          <div className="aspect-[4/3] overflow-hidden rounded-md border border-slate-200 bg-slate-100">
            <img
              src={data.image}
              alt={data.title}
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid={`case-image-${data.slug}`}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src =
                  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=1200";
              }}
            />
          </div>
          <div className="mt-6 space-y-3">
            <MetaRow icon={Briefcase} label="Industry" value={data.industry} />
            <MetaRow icon={MapPin} label="Geography" value={data.geography} />
            <MetaRow icon={ArrowRight} label="Engagement" value={data.engagement} />
            {data.duration && <MetaRow icon={Clock} label="Duration" value={data.duration} />}
          </div>
        </div>

        <div className="lg:col-span-3">
          <p className="eyebrow">Case Study {String(index + 1).padStart(2, "0")}</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#0A1F44] mt-3 tracking-tight leading-tight">
            {data.title}
          </h2>

          <div className="mt-8">
            <SubHead label="The Challenge" />
            <p className="mt-3 text-slate-600 leading-relaxed">{d.challenge}</p>
          </div>

          <div className="mt-8">
            <SubHead label="The Sozos Approach" />
            <ul className="mt-3 space-y-2.5">
              {d.approach.map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <Check className="w-4 h-4 text-[#1E40AF] mt-1 shrink-0" />
                  <span className="text-sm md:text-base leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8">
            <SubHead label="Outcomes" />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {data.impact.map((m) => (
                <div
                  key={m}
                  className="rounded-md bg-blue-50 border border-blue-100 px-4 py-5 text-center"
                >
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-[#1E40AF] leading-tight">
                    {m}
                  </div>
                </div>
              ))}
            </div>
            {d.outcomesIntro && (
              <p className="mt-5 text-sm text-slate-600 leading-relaxed italic">{d.outcomesIntro}</p>
            )}
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100">
            <SubHead label="The Impact" />
            <p className="mt-3 text-slate-700 leading-relaxed">{d.impactStatement}</p>
            <p className="mt-5 text-[#0A1F44] font-semibold tracking-tight">{d.tagline}</p>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          {label}
        </div>
        <div className="text-slate-700 leading-snug">{value}</div>
      </div>
    </div>
  );
}

function SubHead({ label }) {
  return (
    <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#1E40AF]">{label}</p>
  );
}
